/**
 * RecentScanPhotos — Ops Dashboard glance card.
 * Renders the newest tour-scan photos (Detailed-DAR sites) so ops can
 * spot suspicious scans at a glance without drilling into each DAR.
 *
 * Auto-refreshes every 60s. Clicking a tile opens the full-size photo
 * in a new tab.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCw, ExternalLink } from "lucide-react";
import api, { resolveMediaUrl } from "../lib/api";
import { fmtDateTime } from "../lib/tz";
import MediaImage from "./MediaImage";

function resolveSrc(url) {
  return resolveMediaUrl(url);
}

export default function RecentScanPhotos({ limit = 12 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/dashboard/recent-scan-photos?limit=${limit}`);
      setItems(r.data?.items || []);
      setRefreshedAt(new Date());
      setError(null);
    } catch (e) {
      // Surface the failure so admins don't mistake it for a legit empty
      // state. Full page shouldn't fall over on a transient blip.
      setError(e?.response?.data?.detail || e?.message || "Failed to load scan photos");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="border border-[#27272A] bg-[#121212]" data-testid="dashboard-recent-scans">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
        <div>
          <div className="label-mute">Detailed-DAR Snapshots</div>
          <div className="font-display uppercase font-bold text-xl mt-1">Recent Tour-Scan Photos</div>
          <p className="text-zinc-500 text-xs mt-1">
            The freshest photos  captured at their tag scans. Click any tile to open the original.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5"
          data-testid="dashboard-recent-scans-refresh"
        >
          <RotateCw className="size-3.5" /> Refresh
        </button>
      </div>
      <div className="p-5">
        {loading && items.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-8 scanlines" data-testid="dashboard-recent-scans-loading">
            Loading…
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-8 scanlines" data-testid="dashboard-recent-scans-empty">
            No scan photos yet — flip a site into Detailed DAR mode to start collecting them.
          </div>
        )}
        {error && (
          <div
            className="text-sm py-3 px-4 border border-[#FF3B3033] bg-[#FF3B3011] text-[#FF3B30] font-mono"
            data-testid="dashboard-recent-scans-error"
          >
            Failed to load: {error}
          </div>
        )}
        {items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {items.map((it) => {
              const src = resolveSrc(it.photo_url);
              return (
                <a
                  key={it.visit_id}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border border-[#27272A] bg-[#0A0A0A] hover:border-[#FF3B30] transition-colors group"
                  data-testid={`dashboard-scan-tile-${it.visit_id}`}
                  title={`${it.tag_name || "—"} · ${it.site_name || "—"} · ${it.guard_name || "—"}`}
                >
                  <div className="relative">
                    <MediaImage
                      src={src}
                      alt={it.description || it.tag_name || "scan"}
                      className="w-full h-28 object-cover"
                      testId={`dashboard-scan-tile-img-${it.visit_id}`}
                    />
                    <ExternalLink className="absolute top-1 right-1 size-3 text-white/60 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="px-2 py-2 border-t border-[#27272A]">
                    <div className="text-xs font-medium truncate">{it.tag_name || "—"}</div>
                    <div className="text-[10px] text-zinc-500 font-mono truncate">
                      {it.site_name || "—"}
                    </div>
                    <div className="text-[10px] text-zinc-400 font-mono truncate">
                      {it.guard_name || "—"}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono mt-1">
                      {fmtDateTime(it.captured_at)}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
        {refreshedAt && (
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-3 text-right font-mono">
            Refreshed {refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · <Link to="/dar" className="hover:text-zinc-300 underline">Open DAR &rsaquo;</Link>
          </div>
        )}
      </div>
    </div>
  );
}
